import React, { ChangeEvent, useEffect, useId, useRef, useState } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { type NextPage } from 'next';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import z from 'zod';
import { SelectInput } from '../components/select';
import {
  AnimatedInputs,
  Loader,
  NominationHero,
  PageContainer,
  PopUp,
  WinnerCarousel,
} from '../components/index';
import {
  BottomLeftGroup,
  BottomRightGroup,
  Important,
  LeftCenterGroup,
  RightCenterGroup,
  TopLeftGroup,
  TopRightGroup,
  UploadImageIcon,
} from '../components/icons';
import {
  facebook,
  twitter,
  instagram,
  linkedln,
  thumbs,
  close2 as closeIcon,
} from '../public';
import useInput from '../utils/hooks/useInput';
import useToggle, { UseToggle } from '../utils/hooks/useToggle';
import { handleErrorValidation } from '@util/functions/zodParserUtils';
import { NextFont } from 'next/dist/compiled/@next/font';
import { useAcceptNominationMutation } from '@util/services/ApiConnection';
import { Poppins } from 'next/font/google';
import FileInput from '@components/FileUpload';
import { Star } from 'lucide-react';

interface IFormError {
  full_name: boolean;
  linkedln_url: boolean;
  years_of_experience: boolean;
  current_job_title: boolean;
  award: boolean;
  email: boolean;
  user_image: boolean;
}
const inter = Poppins({
  display: 'swap',
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  subsets: ['latin', 'latin-ext'],
});
const Page: NextPage<{ inter: NextFont }> = ({ }) => {
  const [loading, setLoading] = useState(false);
  //   const [popUp, setPopUp] = useState(false);
  const urlRegex = new RegExp(
    /^(https?:\/\/)?(www\.)?(linkedin\.com\/in\/)(\S*)(\/)?$/i
  );
  const [fullName, changeFullName] = useInput('');
  const [email, changeEmail] = useInput('');
  const [user_image, setUser_image] = useState<File | null | undefined>(null);
  const [years_of_experience, changeyears_of_experience] = useInput('');
  const [current_job_title, changeCurrent_job_title] = useInput('');
  const [linkedInInput, changeLinkedInInput] = useInput('');
  const [awardInput, changeAwardInput] = useInput('');
  const [acceptNomination, { isLoading }] = useAcceptNominationMutation();
  const [{ toggle, setToggle }, close] = useToggle(false);
  const ref = useRef<HTMLDivElement>(null);
  const [filled, setFilled] = useState(false);
  const isInVIew = useInView(ref);
  const { push, query } = useRouter();
  const [message, setMessage] = useState<string>(
    'Congratulations on your first step to be a Global Tech Hero'
  );
  const mainControls = useAnimation();
  const [
    { toggle: redirectedToggle, setToggle: setRedirectedToggle },
    redirectedClose,
  ]: UseToggle = useToggle(false);
  useEffect(() => {
    if (query?.payment_successful && query?.status !== 'cancelled') {
      setRedirectedToggle();
    }
  }, [query?.payment_successful]);
  useEffect(() => {
    if (isInVIew) {
      void mainControls.start('visible');
    } else {
      void mainControls.start('hidden');
    }
  }, [isInVIew, mainControls]);
  const initialState = {
    full_name: false,
    linkedln_url: false,
    years_of_experience: false,
    current_job_title: false,
    award: false,
    email: false,
    user_image: false,
  };
  const [error, setError] = useState<IFormError>(initialState);
  const [errorOccured, setErrorOccured] = useState(false);
  const inputSchema = z.object({
    full_name: z.string().min(1),
    linkedln_url: z.string().min(1),
    years_of_experience: z.string().min(1),
    current_job_title: z.string().min(1),
    award: z.string().min(1),
    email: z.string().email().min(1),
    user_image: z.instanceof(Blob),
  });
  const { data: session } = useSession();
  const Id = useId();
  const options = [
    'Data',
    'Design',
    'Development',
    'Growth',
    'Operation',
    'Product',
    'Security',
  ];
  const handleSubmission = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setError(initialState);
    setErrorOccured(false);
    setLoading(true);
    const result = inputSchema.safeParse({
      full_name: fullName,
      linkedln_url: linkedInInput,
      years_of_experience,
      current_job_title,
      award: awardInput,
      email: email.trim(),
      user_image,
    });
    if (!result.success) {
      handleErrorValidation(result.error, setError);
      setLoading(false);
    } else {
      const form_data = new FormData();
      /*`
        {
          full_name: string
          job_title: string
          category: string
          experience: number
          address: string
          email: string
          linkedin: string
          image: blob
          redirect_url: string
          currency: USD
        }
        */
      form_data.append('fullname', result.data.full_name);
      form_data.append('job', result.data.current_job_title);
      form_data.append('category', result.data.award);
      form_data.append(
        'experience',
        result.data.years_of_experience.toString()
      );
      form_data.append('email', result.data.email);
      form_data.append('linkedin', result.data.linkedln_url);
      form_data.append(
        'image',
        result.data.user_image,
        (result.data.user_image as File).name
      );
      form_data.append(
        'redirect_url',
        `http://theconnectedawards.com/participate?payment_successful=true`
      );
      form_data.append('currency', 'GBP');
      fetch('https://www.awardtest.techtrend.africa/api/nominate', {body: form_data, method: 'POST'})
      				.then((res) => {
                setLoading(false);
                return res.json();
              })
        .then((res) => {
          // alert(JSON.stringify(res));
          if (res.status === "success") {
            setFilled(true);
            setRedirectedToggle();
            setMessage(res.message);
          } else {
            setFilled(true);
            setErrorOccured(true);
            setMessage(res.message);
            setRedirectedToggle();
          }
        })
        .catch((err) => {
          setErrorOccured(true);
          setMessage(err?.data?.message);
          setRedirectedToggle();
        });
    }
  };
  const FileDrop = (e: ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0];
    if (newFile) {
      setUser_image(newFile);
    }
  };
  return (
    <PageContainer>
      <NominationHero font={inter} />
      <PopUp
        opened={redirectedToggle}
        close={redirectedClose}
        details={message}
        detailscontd={''}
        icon={errorOccured ? closeIcon : thumbs}
        closeIcon={closeIcon}
        link="/"
        linkText={!errorOccured ? "Return To Home" : undefined}
        header={errorOccured ? "Oops":"Success!"}
      />
      <form
        id="participation_form"
        onSubmit={handleSubmission}
        className={`relative flex h-fit w-full items-center bg-[#F0F0F0] ${inter.className}`}
      >
        <div className="m-auto flex w-full flex-col space-y-10 rounded-lg p-10 py-16 lg:w-1/2 lg:text-center">
          <h1 className="relative text-2xl font-normal tracking-[0.24px] after:absolute after:-bottom-2 after:left-1 after:h-1 after:w-5/12 after:rounded-full after:bg-golden lg:mx-auto lg:text-4xl ">
            Participation Form
          </h1>
          <div className="flex w-full flex-col gap-8">
            <span className="flex w-full flex-col-reverse gap-2 text-left text-sm">
              {error.full_name && (
                <p className="text-xs text-red-400">
                  Please enter nominee's full name
                </p>
              )}
              <AnimatedInputs
                value={fullName}
                onChange={changeFullName}
                name="fullname"
                id="fullname"
                type="text"
                error={error.full_name}
                placeholder="Flora Shaw"
              />
              <label
                className={`${error.full_name
                    ? 'text-red-500 peer-focus:text-red-700'
                    : 'text-[#0c0114]'
                  } cursor-pointer text-sm capitalize transition-all duration-300 ease-in peer-focus:text-base lg:text-base lg:peer-focus:text-lg`}
                htmlFor="fullname"
              >
                Full Name
              </label>
            </span>
            <span className="flex basis-1/2 flex-col-reverse gap-2 text-left">
              {error.email && (
                <p className="text-xs text-red-400">
                  Please enter nominee's email address
                </p>
              )}
              <AnimatedInputs
                value={email}
                onChange={changeEmail}
                name="email"
                id="email"
                type="text"
                placeholder="Florashaw@gmail.com"
                error={error.email}
              />
              <label
                className={`${error.email
                    ? 'text-red-500 peer-focus:text-red-700'
                    : 'text-[#0c0114]'
                  } cursor-pointer text-sm capitalize transition-all duration-300 ease-in peer-focus:text-base lg:text-base lg:peer-focus:text-lg`}
                htmlFor="email"
              >
                Email Address
              </label>
            </span>
            <span className="flex basis-1/2 flex-col-reverse gap-2 text-left">
              {error.linkedln_url && (
                <p className="float-right text-xs text-red-400">
                  Please enter a valid url
                </p>
              )}
              <AnimatedInputs
                value={linkedInInput}
                onChange={changeLinkedInInput}
                type="text"
                id="linkedln"
                name="linkedln"
                error={error.linkedln_url}
                placeholder="www.linkedin.com/in/florashaw/"
              />

              <label
                htmlFor="linkedln"
                className={`flex basis-1/4 items-center gap-2 ${error.linkedln_url
                    ? 'text-red-500 peer-focus:text-red-700'
                    : 'text-[#0c0114]'
                  } cursor-pointer text-sm capitalize peer-focus:text-base lg:text-base lg:peer-focus:text-lg`}
              >
                <span>Linkedin URL</span>
              </label>
            </span>
            <span className="flex w-full basis-1/2 flex-col-reverse gap-2 text-left">
              {error.current_job_title && (
                <p className="text-xs text-red-400">
                  Please enter nominee's job title
                </p>
              )}
              <AnimatedInputs
                value={current_job_title}
                onChange={changeCurrent_job_title}
                name="current_job_title"
                id="current_job_title"
                type="text"
                error={error.current_job_title}
                placeholder="Product designer"
              />
              <label
                className={`${error.current_job_title
                    ? 'text-red-500 peer-focus:text-red-700'
                    : 'text-[#0c0114]'
                  } cursor-pointer text-sm capitalize transition-all duration-300 ease-in peer-focus:text-base lg:text-base lg:peer-focus:text-lg`}
                htmlFor="current_job_title"
              >
                Current job title{' '}
                <span className="my-auto ml-1 inline h-auto w-3">*</span>
              </label>
            </span>
            <span className="flex w-full basis-1/2 flex-col-reverse gap-2 text-left">
              {error.award && (
                <p className="text-xs text-red-400">
                  Please select an award category
                </p>
              )}
              <SelectInput
                value={awardInput}
                onChange={changeAwardInput}
                name="award_category"
                id="award_category"
                className={`peer border outline-none ${error.award
                    ? 'border-red-200 peer-focus:border-red-500'
                    : 'border-pink-main focus:border-gray-300'
                  } rounded-[4px] bg-transparent`}
                options={options}
                placeholder="Select category"
              />
              <label
                className={`${error.award
                    ? 'text-red-500 peer-focus:text-red-700'
                    : 'text-[#0c0114]'
                  } w-full cursor-pointer  text-sm capitalize peer-focus:text-base lg:text-base lg:peer-focus:text-lg`}
                htmlFor="award_category"
              >
                Category{' '}
                <span className="my-auto ml-1 inline h-auto w-3">*</span>
              </label>
            </span>
            <span className="flex w-full basis-1/2 flex-col-reverse gap-2 text-left">
              {error.years_of_experience && (
                <p className="text-xs text-red-400">
                  Please enter nominee's years of experience
                </p>
              )}
              <SelectInput
                value={years_of_experience}
                onChange={changeyears_of_experience}
                options={[
                  'Below 5 years',
                  '5-10 years',
                  '10-15 years',
                  'Above 15 years',
                ]}
                name="years_of_experience"
                id="years_of_experience"
                className={`peer border outline-none ${error.years_of_experience
                    ? 'border-red-200 peer-focus:border-red-500'
                    : 'border-pink-main focus:border-gray-300'
                  } rounded-[4px] bg-transparent`}
                placeholder="Select years of experience"
              />
              <label
                className={`${error.years_of_experience
                    ? 'text-red-500 peer-focus:text-red-700'
                    : 'text-[#0c0114]'
                  } cursor-pointer text-sm capitalize transition-all duration-300 ease-in peer-focus:text-base lg:text-base lg:peer-focus:text-lg`}
                htmlFor="years_of_experience"
              >
                Years of Experience{' '}
                <span className="my-auto ml-1 inline h-auto w-3">*</span>
              </label>
            </span>

            <span className="flex w-full flex-col-reverse gap-2 text-left">
              {error.user_image && (
                <p className="text-xs text-red-400">
                  Please upload a picture of the nominee
                </p>
              )}
              <FileInput file={user_image} onChange={FileDrop} />
            </span>
            <button
              className="mt-3 w-full cursor-pointer rounded bg-darkPurple p-3 text-lightPurple transition-all duration-500 ease-in hover:bg-lightPurple hover:text-darkPurple disabled:cursor-not-allowed disabled:first-letter:opacity-70"
              // onClick={handleSubmission}
              disabled={loading}
              type="submit"
            >
              {!loading ? 'Submit' : 'Loading...'}
            </button>
          </div>
        </div>
      </form>
      <WinnerCarousel font={inter} />
    </PageContainer>
  );
};

export default Page;
